import { useEffect, useState } from "react";

const pulse = `
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Mono:wght@400;500&display=swap');

@keyframes beat {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.06); }
}

@keyframes glow-pulse {
  0%, 100% { opacity: 0.5; filter: blur(18px); }
  50% { opacity: 1; filter: blur(28px); }
}

@keyframes draw-line {
  from { stroke-dashoffset: 400; }
  to { stroke-dashoffset: 0; }
}

@keyframes shimmer {
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
}

@keyframes fade-up {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
`;

export const FirstBeatBadge = ({ size = "default" }) => {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    setTimeout(() => setAnimated(true), 100);
  }, []);

  // Size configurations
  const sizes = {
    small: { width: 80, height: 93, scale: 0.33 },
    default: { width: 240, height: 280, scale: 1 },
    medium: { width: 160, height: 187, scale: 0.67 },
  };

  const { width, height, scale } = sizes[size] || sizes.default;

  return (
    <>
      <style>{pulse}</style>
      <div style={{
        position: "relative",
        width,
        height,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Syne', sans-serif",
      }}>
        {/* Outer ambient glow */}
        <div style={{
          position: "absolute",
          width: 300 * scale,
          height: 300 * scale,
          borderRadius: "50%",
          background: "radial-gradient(circle, #7000ff 0%, #00cfff 50%, transparent 70%)",
          animation: "glow-pulse 2.4s ease-in-out infinite",
          pointerEvents: "none",
        }} />

        {/* Badge */}
        <div style={{
          position: "relative",
          width: 240 * scale,
          height: 280 * scale,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          animation: animated ? "beat 2.4s ease-in-out infinite" : "none",
          cursor: "default",
          userSelect: "none",
        }}>
          {/* Hexagon shape */}
          <svg
            width={240 * scale}
            height={280 * scale}
            viewBox="0 0 240 280"
            style={{ position: "absolute", top: 0, left: 0 }}
          >
            <defs>
              <linearGradient id="hex-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#07001a" />
                <stop offset="100%" stopColor="#000d1a" />
              </linearGradient>
              <linearGradient id="border-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#bf00ff" />
                <stop offset="35%" stopColor="#00cfff" />
                <stop offset="70%" stopColor="#00ffb0" />
                <stop offset="100%" stopColor="#bf00ff" />
              </linearGradient>
              <filter id="inner-glow">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
            </defs>

            {/* Hex border glow */}
            <polygon
              points="120,10 222,67 222,213 120,270 18,213 18,67"
              fill="none"
              stroke="url(#border-grad)"
              strokeWidth="3"
              opacity="0.3"
              filter="url(#inner-glow)"
            />
            {/* Main hex */}
            <polygon
              points="120,14 218,69 218,211 120,266 22,211 22,69"
              fill="url(#hex-grad)"
            />
            {/* Inner border */}
            <polygon
              points="120,14 218,69 218,211 120,266 22,211 22,69"
              fill="none"
              stroke="url(#border-grad)"
              strokeWidth="1.5"
            />
          </svg>

          {/* Content */}
          <div style={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10 * scale,
            paddingTop: 8 * scale,
            transform: `scale(${scale})`,
            transformOrigin: "center center",
          }}>

            {/* Heartbeat EKG line */}
            <svg width="130" height="44" viewBox="0 0 130 44">
              <defs>
                <linearGradient id="line-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#bf00ff" stopOpacity="0" />
                  <stop offset="25%" stopColor="#bf00ff" />
                  <stop offset="55%" stopColor="#00cfff" />
                  <stop offset="80%" stopColor="#00ffb0" />
                  <stop offset="100%" stopColor="#00ffb0" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                d="M0,22 L28,22 L36,22 L42,4 L50,40 L56,12 L62,22 L70,22 L102,22"
                fill="none"
                stroke="url(#line-grad)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="400"
                style={{
                  animation: animated ? "draw-line 1.2s ease-out forwards" : "none",
                  animationDelay: "0.2s",
                  strokeDashoffset: 400,
                }}
              />
              {/* Dot at peak */}
              <circle cx="50" cy="40" r="3" fill="#00ffb0"
                style={{
                  opacity: animated ? 1 : 0,
                  transition: "opacity 0.3s ease 1.2s",
                }}
              />
            </svg>

            {/* FIRST BEAT label */}
            <div style={{
              animation: animated ? "fade-up 0.7s ease forwards" : "none",
              animationDelay: "0.6s",
              opacity: 0,
              textAlign: "center",
            }}>
              <div style={{
                fontSize: 11,
                letterSpacing: "0.3em",
                color: "#00cfff",
                fontFamily: "'DM Mono', monospace",
                fontWeight: 500,
                marginBottom: 4,
                textTransform: "uppercase",
              }}>
                ◆ AUDIVUE ◆
              </div>
              <div style={{
                fontSize: 34,
                fontWeight: 800,
                letterSpacing: "-0.02em",
                lineHeight: 1,
                background: "linear-gradient(135deg, #ffffff 0%, #ffaaaa 50%, #e8344a 100%)",
                backgroundSize: "200% auto",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                animation: "shimmer 3s linear infinite",
              }}>
                FIRST
              </div>
              <div style={{
                fontSize: 34,
                fontWeight: 800,
                letterSpacing: "-0.02em",
                lineHeight: 1,
                background: "linear-gradient(135deg, #ffffff 0%, #ffaaaa 50%, #e8344a 100%)",
                backgroundSize: "200% auto",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                animation: "shimmer 3s linear infinite",
                animationDelay: "0.15s",
              }}>
                BEAT
              </div>
            </div>

            {/* Bottom label */}
            <div style={{
              fontSize: 9,
              letterSpacing: "0.25em",
              color: "rgba(255,255,255,0.3)",
              fontFamily: "'DM Mono', monospace",
              textTransform: "uppercase",
              animation: animated ? "fade-up 0.7s ease forwards" : "none",
              animationDelay: "0.9s",
              opacity: 0,
              marginTop: 2,
            }}>
              Early Adopter
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Mini badge for inline display
export const FirstBeatBadgeMini = () => {
  return (
    <div 
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium"
      style={{
        background: "linear-gradient(135deg, rgba(112, 0, 255, 0.2), rgba(0, 207, 255, 0.2))",
        border: "1px solid rgba(0, 207, 255, 0.3)",
      }}
      title="First Beat - Early Adopter"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path
          d="M2,12 L6,12 L8,4 L12,20 L16,8 L18,12 L22,12"
          stroke="url(#mini-grad)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <defs>
          <linearGradient id="mini-grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#bf00ff" />
            <stop offset="50%" stopColor="#00cfff" />
            <stop offset="100%" stopColor="#00ffb0" />
          </linearGradient>
        </defs>
      </svg>
      <span style={{
        background: "linear-gradient(135deg, #bf00ff, #00cfff)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
      }}>
        First Beat
      </span>
    </div>
  );
};

export default FirstBeatBadge;
